import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import ClosedCaptionOutlinedIcon from '@mui/icons-material/ClosedCaptionOutlined';
import { CompositionStateStore } from '../../../librettoEditor/store/CompositionStateStore';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ApplicationStore } from '../../../librettoEditor/store/ApplicationStore';
import { ContextMenuStore } from '../../../librettoEditor/store/ContextMenuStore';
import { TimelineStore } from '../../../librettoEditor/store/TimelineStore';
import { convertAbsTimeToUnits, convertTimeToUnits } from '../../../librettoEditor/utils/dom/convertTimeToUnits';
import { CLIP_CLASSNAME } from '../../../librettoEditor/modules/timeline/config/constants';
import { generateClipIdentifierClass } from '../../../librettoEditor/modules/timeline/utils/generateClipIdentifierClass';
import clsx from 'clsx';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';
import { ProjectAssetsStore } from '../../../state/ProjectAssetsStore';
import {
    dispatchRecordIntoTranscriptRequest,
    resolveScriptLocalTimeForRecording,
    resolveTimelineRecordAnchor,
} from '../V2TextEditor/recordIntoTranscriptRequest';

interface CaptionsTimelineClipProps {
    clipId: string;
    layerId: string;
}

/**
 * Timeline clip component for CaptionsClip.
 * Displays as a thin bar with a captions icon and "Captions" label.
 * Custom implementation (not using generic Clip) for better control over context menu.
 */
const CaptionsTimelineClip: React.FC<CaptionsTimelineClipProps> = observer(({ clipId, layerId }) => {
    const [startTime, setStartTime] = useState(0);
    const [trimmedDuration, setTrimmedDuration] = useState(0);
    const [isFocused, setIsFocused] = useState(false);
    const [zoom, setZoom] = useState(TimelineStore.zoom);
    const elementRef = useRef<HTMLDivElement>(null);

    // Watch for clip state changes
    useEffect(() => {
        const disposer = reaction(
            () => ({
                clip: CompositionStateStore.clips?.[clipId],
                selectedClipId: ApplicationStore.selectedClipId,
                zoom: TimelineStore.zoom,
            }),
            ({ clip, selectedClipId, zoom: newZoom }) => {
                if (!clip) return;
                setStartTime(clip.startTime);
                setTrimmedDuration(clip.trimmedDuration);
                setIsFocused(selectedClipId === clip.id);
                setZoom(newZoom);
            },
            { fireImmediately: true }
        );
        return () => disposer();
    }, [clipId]);

    // Calculate position and width based on timeline zoom
    const x = convertAbsTimeToUnits(startTime);
    const width = convertTimeToUnits(trimmedDuration);

    const handleMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        ApplicationStore.setSelectedClipId(clipId);
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Select this clip on right-click if not already selected
        if (ApplicationStore.selectedClipId !== clipId) {
            ApplicationStore.setSelectedClipId(clipId);
        }

        const menuItems = [];

        if (!ApplicationStore.rangeToolEnabled && elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const relativeX = e.clientX - rect.left;
            const fraction = rect.width > 0
                ? Math.max(0, Math.min(1, relativeX / rect.width))
                : 0;
            const absoluteTimeSec = startTime + fraction * trimmedDuration;
            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            const requestedScriptLocalSec = resolveScriptLocalTimeForRecording(
                scriptClip,
                absoluteTimeSec,
            );

            if (scriptClip && requestedScriptLocalSec != null) {
                const scriptClipId = String(scriptClip?.id ?? "") || null;
                const transcriptWords = (ScriptLayerStore.transcriptWords ?? []).map((word: any) => ({
                    text: String(word?.text ?? ""),
                    payload: { ...word, clipId: scriptClipId ?? undefined },
                }));
                const anchor = resolveTimelineRecordAnchor(
                    requestedScriptLocalSec,
                    transcriptWords,
                );

                if (anchor) {
                    let compositionId = ProjectAssetsStore.projectCurrentCompositionId;
                    try {
                        compositionId =
                            String(
                                (CompositionService.composition as any)?.compositionId ??
                                    compositionId ??
                                    "",
                            ) || null;
                    } catch {}

                    const dispatchRecording = (mediaKind: "audio" | "video") => {
                        dispatchRecordIntoTranscriptRequest({
                            source: "timeline-context-menu",
                            mediaKind,
                            projectId: ProjectAssetsStore.projectId,
                            compositionId,
                            scriptClipId,
                            anchor,
                        });
                        ContextMenuStore.hide();
                    };

                    menuItems.push({
                        title: "Record into transcript",
                        children: [
                            { title: "Audio", onClick: () => dispatchRecording("audio") },
                            { title: "Video", onClick: () => dispatchRecording("video") },
                        ],
                    });
                }
            }
        }

        // Layer order submenu (captions are visual elements)
        menuItems.push({
            title: "Layer order",
            children: [
                { 
                    title: "Bring to front", 
                    onClick: () => { 
                        CompositionService.bringVisualToFront(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Bring forward", 
                    onClick: () => { 
                        CompositionService.bringVisualForward(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send backward", 
                    onClick: () => { 
                        CompositionService.sendVisualBackward(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send to back", 
                    onClick: () => { 
                        CompositionService.sendVisualToBack(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
            ],
        });

        // Delete
        menuItems.push({
            title: "Delete",
            onClick: () => {
                CompositionService.deleteClipById(clipId);
                ContextMenuStore.hide();
            },
        });

        ContextMenuStore.show(e.clientX, e.clientY, menuItems);
    };

    return (
        <div
            ref={elementRef}
            id={clipId}
            className={clsx(CLIP_CLASSNAME, 'clip--captions', generateClipIdentifierClass(clipId), {
                clip__focused: isFocused,
            })}
            style={{
                transform: `translateX(${x}px)`,
                width: `${width}px`,
            }}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
        >
            <ClosedCaptionOutlinedIcon
                className="captions-timeline-clip__icon"
                sx={{
                    fontSize: 14,
                    flexShrink: 0,
                }}
            />
            <span
                className="captions-timeline-clip__label"
            >
                Captions
            </span>
        </div>
    );
});

export default CaptionsTimelineClip;
